import React, { useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import { Flex } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { Input } from '@morressier/alembic';
import { FieldLabel } from 'src/components/Field';
import { FormFieldDependentAutoSuggest as IFormFieldDependentAutoSuggest } from '@publishing-engine/domain/types/article';
import FormFieldErrorMessage from './common/FormFieldErrorMessage';
import AutosuggestSelect from './AutosuggestSelect';
import { autosuggestSourceSearchMethod } from 'src/api/autosuggest';

interface IFormFieldDependentAutoSuggestProps {
  config: IFormFieldDependentAutoSuggest;
  isEditingAllowed?: boolean;
  /**
   * Row index used to substitute the `{index}` token in `caseDataFields.main`
   * and `autosuggest.parentField`. Optional for non-repeatable fields.
   */
  index?: number | string;
}

const substituteIndex = (path: string, index?: number | string) =>
  index !== undefined ? path.replaceAll('{index}', String(index)) : path;

const FormFieldDependentAutoSuggest: React.FC<
  IFormFieldDependentAutoSuggestProps
> = ({ config, isEditingAllowed = true, index }) => {
  const {
    formState: { errors },
    watch,
    setValue
  } = useFormContext();

  const {
    name,
    params: { labels, validations, autosuggest },
    caseDataFields: { main }
  } = config;

  const mainField = substituteIndex(main, index);

  const parentFields = React.useMemo(() => {
    if (!autosuggest.parentField) return [];
    const raw = autosuggest.parentField;
    const list = Array.isArray(raw) ? raw : [raw];
    return list.map(path => substituteIndex(path, index));
  }, [autosuggest.parentField, index]);

  const termIdPath = autosuggest.termIdPath ?? 'termId';
  const isDependent = autosuggest.source === 'byTermId';
  const isFreeText = autosuggest.source === 'freeText';

  const formValues = watch();
  const value = get(formValues, mainField);

  // Resolve the first parent candidate that exposes a non-empty termId.
  let termSearch: string | undefined;
  if (isDependent) {
    for (const parentField of parentFields) {
      const parentValue = get(formValues, parentField);
      const termId = get(parentValue, termIdPath);
      if (termId !== undefined && termId !== null && termId !== '') {
        termSearch = String(termId);
        break;
      }
    }
  }

  const isDisabled = isDependent ? !termSearch : false;
  const error = get(errors, mainField) as { message?: string } | undefined;

  // Clear our own value whenever the resolved parent termId changes (or the
  // parent is cleared). Skipped on first render so an already-populated value
  // is not wiped on mount.
  const prevTermSearch = useRef(termSearch);
  useEffect(() => {
    if (prevTermSearch.current === termSearch) return;
    prevTermSearch.current = termSearch;
    if (isDependent) {
      setValue(mainField, undefined, {
        shouldDirty: false,
        shouldValidate: false
      });
    }
  }, [termSearch, isDependent, mainField, setValue]);

  // Inline free-text affordance for `allowFreeText` (or a fully free-text
  // `source: 'freeText'` field): toggles between the CV autosuggest and a plain
  // text input, storing the same `{ termId, name, isCustom }` shape.
  const [freeTextMode, setFreeTextMode] = useState(isFreeText);
  const handleFreeTextChange = (text: string) => {
    const current = get(formValues, mainField);
    const termId =
      current?.isCustom && current.termId
        ? current.termId
        : `custom_${Math.random().toString(36).slice(2)}`;
    setValue(
      mainField,
      { termId, name: text, isCustom: true },
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const placeholder = labels.placeholder ?? labels.main;

  // `FormFieldErrorMessage` resolves `caseDataFields.main` itself, so hand it
  // the index-substituted path.
  const errorConfig = { ...config, caseDataFields: { main: mainField } };

  return (
    <Flex flexDirection="column" gap={2}>
      <FieldLabel
        isRequired={validations.required}
        label={labels.main}
        subText={labels.subtitle}
        helpText={labels.helpText}
      />

      {autosuggest.source === 'freeText' || freeTextMode ? (
        <Input
          name={name}
          value={get(formValues, mainField)?.name ?? ''}
          onChange={e => handleFreeTextChange(e.target.value)}
          isInvalid={!!error}
          isReadOnly={!isEditingAllowed}
          placeholder={placeholder}
        />
      ) : (
        <AutosuggestSelect
          value={value}
          onChange={item =>
            setValue(mainField, item, {
              shouldDirty: true,
              shouldValidate: true
            })
          }
          searchMethod={autosuggestSourceSearchMethod[autosuggest.source]}
          queryKey={mainField}
          termSearch={termSearch}
          isDisabled={isDisabled || !isEditingAllowed}
          isInvalid={!!error}
          placeholder={placeholder}
          limit={autosuggest.limit}
          level={autosuggest.level}
          allowNewKeyword={autosuggest.allowFreeText}
          onNewKeywordClick={
            autosuggest.allowFreeText ? () => setFreeTextMode(true) : undefined
          }
          newKeywordLabel={labels.main}
        />
      )}

      <FormFieldErrorMessage config={errorConfig} />
    </Flex>
  );
};

export default FormFieldDependentAutoSuggest;
